import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  UsersIcon,
  TruckIcon,
  MapIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';

const Dashboard = () => {
  const { currentUser } = useAuth();
  const [stats, setStats] = useState({
    reservations: { total: 0, pending: 0, confirmed: 0, completed: 0, cancelled: 0 },
    transfers: { total: 0, pending: 0, inProgress: 0, completed: 0 },
    agencies: { total: 0, active: 0 },
    carriers: { total: 0, active: 0 },
    regions: { total: 0 },
    vehicles: { total: 0, active: 0 },
    finance: { income: '0', expense: '0', balance: '0' }
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Normally, we would fetch dashboard data from the API
    // For now, we'll use mock data
    const fetchDashboardData = () => {
      setTimeout(() => {
        setStats({
          reservations: { total: 0, pending: 0, confirmed: 0, completed: 0, cancelled: 0 },
          transfers: { total: 0, pending: 0, inProgress: 0, completed: 0 },
          agencies: { total: 0, active: 0 },
          carriers: { total: 0, active: 0 },
          regions: { total: 0 },
          vehicles: { total: 0, active: 0 },
          finance: { income: '₺0', expense: '₺0', balance: '₺0' }
        });
        setIsLoading(false);
      }, 1000);
    };

    fetchDashboardData();
  }, []);

  const StatCard = ({ title, value, icon: Icon, change, changeType, linkTo }) => (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <Icon className="h-6 w-6 text-gray-400" aria-hidden="true" />
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
              <dd>
                <div className="text-lg font-medium text-gray-900">{value}</div>
              </dd>
            </dl>
          </div>
        </div>
      </div>
      <div className="bg-gray-50 px-5 py-3">
        <div className="text-sm">
          {change && (
            <span className={`mr-2 inline-flex items-center ${changeType === 'increase' ? 'text-green-600' : 'text-red-600'}`}>
              {changeType === 'increase' ? (
                <ArrowUpIcon className="h-4 w-4 mr-1" />
              ) : (
                <ArrowDownIcon className="h-4 w-4 mr-1" />
              )}
              {change}
            </span>
          )}
          <Link to={linkTo} className="font-medium text-primary-600 hover:text-primary-500">
            Detayları Görüntüle
          </Link>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
        <div className="mt-3 sm:mt-0 sm:ml-4">
          <p className="text-sm font-medium text-gray-500">
            Hoş geldiniz, {currentUser?.fullName || 'Kullanıcı'}
          </p>
        </div>
      </div>

      <div className="mt-6">
        <h2 className="text-lg font-medium text-gray-900">Genel Bakış</h2>

        <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard
            title="Toplam Rezervasyon"
            value={stats.reservations.total}
            icon={CalendarIcon}
            change="12% artış"
            changeType="increase"
            linkTo="/reservations"
          />

          <StatCard
            title="Aktif Acenteler"
            value={`${stats.agencies.active}/${stats.agencies.total}`}
            icon={UsersIcon}
            linkTo="/agencies"
          />

          <StatCard
            title="Aktif Taşımacılar"
            value={`${stats.carriers.active}/${stats.carriers.total}`}
            icon={TruckIcon}
            linkTo="/carriers"
          />

          <StatCard
            title="Toplam Bölge"
            value={stats.regions.total}
            icon={MapIcon}
            linkTo="/regions"
          />

          <StatCard
            title="Aktif Araçlar"
            value={`${stats.vehicles.active}/${stats.vehicles.total}`}
            icon={TruckIcon}
            linkTo="/vehicles"
          />

          <StatCard
            title="Fiyatlar"
            value="Fiyat Yönetimi"
            icon={CurrencyDollarIcon}
            linkTo="/prices"
          />

          <StatCard
            title="Finansal Durum"
            value={stats.finance.balance}
            icon={CurrencyDollarIcon}
            change={stats.finance.income}
            changeType="increase"
            linkTo="/finance"
          />
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Rezervasyon Durumu */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Rezervasyon Durumu</h3>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div className="col-span-1">
                <div className="text-sm font-medium text-gray-500">Bekleyen</div>
                <div className="mt-1 text-3xl font-semibold text-yellow-500">{stats.reservations.pending}</div>
              </div>
              <div className="col-span-1">
                <div className="text-sm font-medium text-gray-500">Onaylı</div>
                <div className="mt-1 text-3xl font-semibold text-blue-500">{stats.reservations.confirmed}</div>
              </div>
              <div className="col-span-1">
                <div className="text-sm font-medium text-gray-500">Tamamlanan</div>
                <div className="mt-1 text-3xl font-semibold text-green-500">{stats.reservations.completed}</div>
              </div>
              <div className="col-span-1">
                <div className="text-sm font-medium text-gray-500">İptal</div>
                <div className="mt-1 text-3xl font-semibold text-red-500">{stats.reservations.cancelled}</div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-4 sm:px-6 rounded-b-lg">
            <div className="text-sm">
              <Link to="/reservations" className="font-medium text-primary-600 hover:text-primary-500">
                Tüm rezervasyonları görüntüle
              </Link>
            </div>
          </div>
        </div>

        {/* Transfer Durumu */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Transfer Durumu</h3>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <div className="col-span-1">
                <div className="text-sm font-medium text-gray-500">Bekleyen</div>
                <div className="mt-1 text-3xl font-semibold text-yellow-500">{stats.transfers.pending}</div>
              </div>
              <div className="col-span-1">
                <div className="text-sm font-medium text-gray-500">Devam Eden</div>
                <div className="mt-1 text-3xl font-semibold text-blue-500">{stats.transfers.inProgress}</div>
              </div>
              <div className="col-span-1">
                <div className="text-sm font-medium text-gray-500">Tamamlanan</div>
                <div className="mt-1 text-3xl font-semibold text-green-500">{stats.transfers.completed}</div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-4 sm:px-6 rounded-b-lg">
            <div className="text-sm">
              <Link to="/operations" className="font-medium text-primary-600 hover:text-primary-500">
                Tüm transferleri görüntüle
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
